import { useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { PortalForecastRow } from '@proerp/shared';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  FormField,
  SelectNative,
  type ChartConfig,
} from '@proerp/ui';
import { formatQuantity } from '@/lib/formatForecast';
import { articleOptions, articleSeries } from '@/lib/portalMetrics';
import { prefersReducedMotion } from '@/lib/theme';

/**
 * Mengenverlauf **eines** Artikels über die Monate (ADR 0085).
 *
 * Warum nur einer: Innerhalb eines Artikels ist die Einheit konstant — hier
 * darf zu einer Zahl je Monat summiert werden. Mehrere Artikel in einer
 * Y-Achse zu zeigen, würde Stück gegen Kilogramm auftragen und wäre eine
 * hübsche Lüge.
 *
 * Die Auswahl ist ein natives Select (`SelectNative`, CLAUDE.md §9): Auf dem
 * Telefon ist der Systemauswähler das beste Bedienelement, das es gibt.
 */
const config: ChartConfig = { quantity: { label: 'Menge', color: 'var(--chart-1)' } };

export function ArticleTrendChart({ rows }: { rows: readonly PortalForecastRow[] }) {
  const options = articleOptions(rows);
  const [selected, setSelected] = useState('');
  const articleId = selected || options[0]?.articleId || '';
  const active = options.find((option) => option.articleId === articleId);
  const points = articleId ? articleSeries(rows, articleId) : [];
  // s. DemandByMonthChart: recharts animiert per JavaScript, nicht per CSS.
  const isAnimated = !prefersReducedMotion();

  if (options.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <FormField label="Artikel" htmlFor="trendArticle">
        <SelectNative
          id="trendArticle"
          value={articleId}
          onChange={(event) => setSelected(event.target.value)}
        >
          {options.map((option) => (
            <option key={option.articleId} value={option.articleId}>
              {option.label}
            </option>
          ))}
        </SelectNative>
      </FormField>

      {points.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Für diesen Artikel ist noch kein Termin geplant.
        </p>
      ) : (
        <ChartContainer config={config} className="aspect-auto h-[220px] w-full">
          <LineChart data={points} margin={{ left: 4, right: 8, top: 8 }}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={12} />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={48}
              tickFormatter={(value: number) => formatQuantity(value)}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  indicator="dot"
                  formatter={(value) => (
                    <div className="flex w-full items-center justify-between gap-4">
                      <span className="text-muted-foreground">Menge</span>
                      <span className="font-medium tabular-nums text-foreground">
                        {formatQuantity(Number(value))}
                        {active?.unit ? ` ${active.unit}` : ''}
                      </span>
                    </div>
                  )}
                />
              }
            />
            <Line
              dataKey="quantity"
              // monotone: Der Spline schwingt nicht über die Datenpunkte hinaus
              // — sonst zeigte die Kurve zwischen zwei Terminen Mengen an, die
              // niemand geplant hat.
              type="monotone"
              stroke="var(--color-quantity)"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={isAnimated}
            />
          </LineChart>
        </ChartContainer>
      )}
    </div>
  );
}
